import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Alert,
  Tooltip,
  useTheme,
  Stack,
  Fab,
  Zoom,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  DeleteSweep as BulkDeleteIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useDoctorShiftList } from '../hooks/useDoctorShiftList';
import type { DoctorShiftFilterOptions } from '../types/doctorShift';
import { useNotification } from '../hooks/useNotification';
import { DoctorShiftTable } from '../components/DoctorShift/DoctorShiftTable';
import { DoctorShiftEditModal } from '../components/DoctorShift/DoctorShiftEditModal';
import { DoctorShiftDetailModal } from '../components/DoctorShift/DoctorShiftDetailModal';
import { DoctorShiftDeleteDialog } from '../components/DoctorShift/DoctorShiftDeleteDialog';
import { DoctorShiftFilterDrawer } from '../components/DoctorShift/DoctorShiftFilterDrawer';
import './ManagementList.css';

/**
 * 医師シフト管理ページ
 * 
 * 意図：
 * - 医師シフトデータの一覧表示と管理
 * - 高度な検索・フィルタリング機能の提供
 * - CRUD操作の統合UI
 * - CSVアップロードページへの導線
 * 
 * 注意点：
 * - パフォーマンス最適化
 * - エラーハンドリング
 * - レスポンシブ対応
 * 
 * 改善提案：
 * - リアルタイム更新機能
 * - エクスポート機能
 * - 一括操作の拡張
 */
export const DoctorShiftListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // URLクエリの初期フィルター（月次医師シフトのバークリック遷移などから渡される）
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(() => {
    const filters: Partial<DoctorShiftFilterOptions> = {};
    const doctorIds = searchParams.get('doctor_ids');
    if (doctorIds) filters.doctor_ids = doctorIds.split(',').filter(Boolean);
    const startDate = searchParams.get('start_date');
    if (startDate) filters.start_date = startDate;
    const endDate = searchParams.get('end_date');
    if (endDate) filters.end_date = endDate;
    const timeRangeStart = searchParams.get('time_range_start');
    if (timeRangeStart) filters.time_range_start = timeRangeStart;
    const timeRangeEnd = searchParams.get('time_range_end');
    if (timeRangeEnd) filters.time_range_end = timeRangeEnd;
    return filters;
    // 初期表示時のURLクエリのみ反映する（以後のフィルター操作はフックの状態が正）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 医師シフト管理フック
  const {
    data,
    meta,
    isLoading,
    error,
    filters,
    selectedShift,
    editModalOpen,
    detailModalOpen,
    deleteDialogOpen,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,
    showDetails,
    showEditModal,
    showDeleteDialog,
    closeModals,
    createShift,
    updateShift,
    deleteShift,
    bulkDeleteShifts,
    clearError,
  } = useDoctorShiftList(initialFilters);

  // ローカル状態
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // 検索実行
  const handleSearchSubmit = () => {
    handleSearch(searchQuery);
  };

  // 検索フィールドでのEnterキー処理
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // CSVアップロードページへの遷移
  const handleNavigateToUpload = () => {
    navigate('/doctors/shift-upload');
  };

  // 新規作成
  const handleCreate = () => {
    showEditModal();
  };

  // 編集
  const handleEdit = (shift: any) => {
    showEditModal(shift);
  };

  // 編集・新規作成の保存
  const handleSave = async (data: any) => {
    try {
      const success = selectedShift 
        ? await updateShift(selectedShift.id, data)
        : await createShift(data);
      
      if (success) {
        showNotification(
          'success',
          selectedShift ? 'シフトを更新しました' : 'シフトを作成しました'
        );
      }
      return success;
    } catch (error) {
      showNotification('error', '操作に失敗しました');
      return false;
    }
  };

  // 削除
  const handleDelete = async () => {
    if (!selectedShift) return false;
    
    try {
      const success = await deleteShift(selectedShift.id);
      if (success) {
        showNotification('success', 'シフトを削除しました');
      }
      return success;
    } catch (error) {
      showNotification('error', '削除に失敗しました');
      return false;
    }
  };

  // 一括削除
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      const success = await bulkDeleteShifts(selectedIds);
      if (success) {
        showNotification('success', `${selectedIds.length}件のシフトを削除しました`);
        setSelectedIds([]);
      }
    } catch (error) {
      showNotification('error', '一括削除に失敗しました');
    }
  };

  // フィルター適用
  const handleFilterApply = () => {
    // 再取得は onFiltersChange（updateFilters）が適用後のフィルターで実行済み。
    // ここで refresh() を呼ぶと適用前の古いフィルターで再取得し、
    // 結果とフィルター状態を上書きしてしまう（フィルターが効かない原因だった）
  };

  // フィルターリセット
  const handleFilterReset = () => {
    updateFilters({
      search: undefined,
      start_date: undefined,
      end_date: undefined,
      time_range_start: undefined,
      time_range_end: undefined,
      doctor_ids: undefined,
      page: 1,
    });
    setSearchQuery('');
  };

  // 詳細表示から編集への遷移
  const handleDetailToEdit = () => {
    closeModals();
    if (selectedShift) {
      showEditModal(selectedShift);
    }
  };

  return (
    <Box className="management-page" sx={{ p: 0, minWidth: 0, maxWidth: 'none' }}>
      {/* ヘッダーセクション */}
      <div>
        <div className="management-heading">
          <div><h1>医師シフト管理</h1><p>医師のシフト情報を管理します。検索・フィルタリング機能を使用して効率的にデータを確認できます。</p></div>
          <div className="management-heading-actions">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refresh} disabled={isLoading}>更新</Button>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleNavigateToUpload}>CSVアップロード</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>新規作成</Button>
          </div>
        </div>
      </div>

      {/* エラー表示 */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ marginBottom: 24 }}
          >
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ borderRadius: 2 }}
            >
              {error}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 操作パネル */}
      <div>
        <Paper
          className="management-panel"
          elevation={0}
          sx={{
            p: 0,
            mb: 1.5,
            borderRadius: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            className="reservation-search-controls"
          >
            {/* 検索バー */}
            <Box className="admin-shift-search" sx={{ flex: 1, maxWidth: { xs: '100%', md: 400 } }}>
              <TextField
                fullWidth
                placeholder="医師IDで検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
              />
            </Box>
            <Button variant="contained" onClick={handleSearchSubmit} disabled={isLoading}>検索</Button>

            {/* アクションボタン */}
            <Stack direction="row" spacing={1}>
              {/* 一括削除ボタン */}
              {selectedIds.length > 0 && (
                <Tooltip title={`選択中の${selectedIds.length}件を削除`}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<BulkDeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={isLoading}
                    sx={{ borderRadius: 2 }}
                  >
                    削除 ({selectedIds.length})
                  </Button>
                </Tooltip>
              )}

              {/* 詳細な検索条件は既存のドロワーで編集する */}
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setFilterDrawerOpen(true)}
                >
                  詳細絞り込み
                </Button>
            </Stack>
          </Stack>
        </Paper>
      </div>

      {/* データテーブル */}
      <div>
        <section className="management-panel" aria-label="医師シフト一覧">
          <div className="management-results-head"><h2>検索結果</h2><span>{meta?.total_records ?? data.length} 件のシフト</span></div>
        <DoctorShiftTable
          data={data}
          isLoading={isLoading}
          onViewDetails={showDetails}
          onEdit={handleEdit}
          onDelete={showDeleteDialog}
          filters={filters}
          onFiltersChange={updateFilters}
          meta={meta || undefined}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
        </section>
      </div>

      {/* フローティングアクションボタン（モバイル用） */}
      <Zoom in={true}>
        <Fab
          color="primary"
          onClick={handleCreate}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <AddIcon />
        </Fab>
      </Zoom>

      {/* モーダル・ダイアログ */}
      <DoctorShiftEditModal
        open={editModalOpen}
        onClose={closeModals}
        onSave={handleSave}
        shift={selectedShift}
        isLoading={isLoading}
      />

      <DoctorShiftDetailModal
        open={detailModalOpen}
        onClose={closeModals}
        onEdit={handleDetailToEdit}
        shift={selectedShift}
      />

      <DoctorShiftDeleteDialog
        open={deleteDialogOpen}
        onClose={closeModals}
        onConfirm={handleDelete}
        shift={selectedShift}
        isLoading={isLoading}
      />

      <DoctorShiftFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFiltersChange={updateFilters}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
      />
    </Box>
  );
};
